import React, { useState, useRef, useEffect } from 'react';
import { PlusCircle, Coins, LogIn, UserPlus, LayoutDashboard, Sparkles, Layers3, Sofa, PenTool, Globe, MapPinned, Film, ChevronDown, Shield, LogOut, Settings, Users, Gift, Handshake, LayoutGrid, Wrench, Monitor, MoonStar, Video, Clapperboard, Scissors, SlidersHorizontal, Receipt, Loader2, CircleDot, Waves, FileText, FilePen } from 'lucide-react';
import { UserProfile } from '../types';
import { supabase, openBillingPortal } from '../services/supabase';
import { LanguageSwitcher, useTranslation, type LanguageCode } from '@rehub/i18n';

const HyperDuskSwitcherIcon = ({ size = 18, className }: { size?: string | number; className?: string }) => (
  <MoonStar size={Number(size)} className={className} strokeWidth={1.8} />
);

// Apps grouped the way the Hub buckets them, so the switcher gives context.
// Each group carries an accent colour + gradient used to theme its section.
const APP_GROUPS = [
  {
    id: 'workspace', label: 'Workspace',
    accent: 'text-purple-300', iconBg: 'bg-purple-500/15 text-purple-300', gradient: 'from-purple-500/20',
    apps: [
      { id: 'hub', label: 'Hub', icon: LayoutDashboard, url: 'https://hub.rehub.studio' },
      { id: 'flow', label: 'Flow', icon: Waves, url: 'https://flow.rehub.studio' },
      { id: 'studio', label: 'Studio', icon: Clapperboard, url: 'https://studio.rehub.studio' },
      { id: 'assistant', label: 'Assistant', icon: Sparkles, url: 'https://assistant.rehub.studio/assistant?sourceApp=photoclear' },
    ],
  },
  {
    id: 'photo', label: 'AI Photo',
    accent: 'text-sky-300', iconBg: 'bg-sky-500/15 text-sky-300', gradient: 'from-sky-500/20',
    apps: [
      { id: 'photoclear', label: 'Photoclear', icon: Sparkles, url: 'https://photoclear.rehub.studio' },
      { id: 'hdrphoto', label: 'HDR Photo', icon: Layers3, url: 'https://hdrphoto.rehub.studio' },
      { id: 'photoedit', label: 'PhotoEdit', icon: SlidersHorizontal, url: 'https://photoedit.rehub.studio' },
      { id: 'staging', label: 'Staging', icon: Sofa, url: 'https://staging.rehub.studio' },
    ],
  },
  {
    id: 'video', label: 'AI Video',
    accent: 'text-fuchsia-300', iconBg: 'bg-fuchsia-500/15 text-fuchsia-300', gradient: 'from-fuchsia-500/20',
    apps: [
      { id: 'imagemotion', label: 'Imagemotion', icon: Film, url: 'https://imagemotion.rehub.studio' },
      { id: 'videomotion', label: 'Videomotion', icon: Video, url: 'https://videomotion.rehub.studio' },
      { id: 'propertycut', label: 'PropertyCuts', icon: Scissors, url: 'https://propertycuts.rehub.studio' },
      { id: 'hyperdusk', label: 'Hyper Dusk', icon: HyperDuskSwitcherIcon, url: 'https://hyperdusk.rehub.studio' },
      { id: 'record', label: 'Screen Record', icon: CircleDot, url: 'https://record.rehub.studio' },
    ],
  },
  {
    id: 'tours', label: 'Tours & Floor Plans',
    accent: 'text-emerald-300', iconBg: 'bg-emerald-500/15 text-emerald-300', gradient: 'from-emerald-500/20',
    apps: [
      { id: 'tours', label: '360 Tours', icon: Globe, url: 'https://360tours.rehub.studio' },
      { id: 'planos', label: 'Planos', icon: MapPinned, url: 'https://planos.rehub.studio' },
      { id: 'sketchplan', label: 'Sketchplan', icon: PenTool, url: 'https://sketchplan.rehub.studio' },
    ],
  },
  {
    id: 'present', label: 'Presentations',
    accent: 'text-amber-300', iconBg: 'bg-amber-500/15 text-amber-300', gradient: 'from-amber-500/20',
    apps: [
      { id: 'showcase', label: 'Showcase', icon: Monitor, url: 'https://showcase.rehub.studio' },
      { id: 'brochures', label: 'BrochurePDF', icon: FileText, url: 'https://brochures.rehub.studio' },
      { id: 'pdf', label: 'ReHub PDF', icon: FilePen, url: 'https://pdf.rehub.studio' },
    ],
  },
  {
    id: 'tools', label: 'Free Tools',
    accent: 'text-slate-300', iconBg: 'bg-slate-500/15 text-slate-300', gradient: 'from-slate-500/20',
    apps: [
      { id: 'tools', label: 'Tools', icon: Wrench, url: 'https://tools.rehub.studio' },
    ],
  },
] as const;

const APPS = APP_GROUPS.flatMap((g) => g.apps as readonly (typeof APP_GROUPS)[number]['apps'][number][]);
type AppId = typeof APP_GROUPS[number]['apps'][number]['id'];

const handleAppNavigation = async (targetApp: AppId, url: string, currentApp: AppId, e: React.MouseEvent) => {
  if (targetApp === currentApp) {
    e.preventDefault();
    return;
  }
  try {
    const { supabase } = await import('../services/supabase');
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.access_token && session?.refresh_token) {
      e.preventDefault();
      window.location.href =
        `${url}#sso_access_token=${encodeURIComponent(session.access_token)}` +
        `&sso_refresh_token=${encodeURIComponent(session.refresh_token)}`;
    }
  } catch (err) {
    console.warn('SSO navigation failed, using default navigation');
  }
};

const ProductSwitcher: React.FC<{ currentApp: AppId }> = ({ currentApp }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, []);

  return (
    <div className="relative hidden md:block" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 px-3 py-2 rounded-xl bg-slate-800/90 border border-slate-600/50 text-slate-200 hover:text-white hover:bg-slate-700/80 transition-all text-sm font-medium"
        aria-expanded={open}
        aria-haspopup="true"
      >
        <LayoutGrid size={16} />
        <span>Apps</span>
        <ChevronDown size={14} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute left-0 top-full mt-2 w-[340px] max-h-[80vh] overflow-y-auto rounded-2xl border border-slate-700 bg-slate-900 shadow-2xl z-50">
          <div className="relative overflow-hidden rounded-2xl">
            <div aria-hidden className="pointer-events-none absolute inset-0 bg-[radial-gradient(120%_80%_at_0%_0%,rgba(168,85,247,0.18),transparent_55%),radial-gradient(120%_80%_at_100%_100%,rgba(56,189,248,0.12),transparent_55%)]" />
            <div className="relative p-3 space-y-3">
              {APP_GROUPS.map((group) => (
                <div key={group.id}>
                  <div className={`px-2 pb-1.5 text-[10px] font-semibold uppercase tracking-wider ${group.accent}`}>{group.label}</div>
                  <div className="grid grid-cols-2 gap-1.5">
                    {group.apps.map((app) => (
                      <a
                        key={app.id}
                        href={app.url}
                        onClick={(e) => { handleAppNavigation(app.id, app.url, currentApp, e); setOpen(false); }}
                        className={`flex items-center gap-2.5 px-2.5 py-2.5 rounded-xl text-sm font-medium transition-all ${
                          currentApp === app.id
                            ? `bg-gradient-to-br ${group.gradient} to-transparent text-white ring-1 ring-inset ring-white/15`
                            : 'text-slate-300 hover:bg-slate-800/80 hover:text-white'
                        }`}
                      >
                        <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${group.iconBg}`}>
                          <app.icon size={16} strokeWidth={1.8} />
                        </span>
                        <span className="truncate">{app.label}</span>
                      </a>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const MobileAppSwitcher: React.FC<{ currentApp: AppId }> = ({ currentApp }) => (
  <div className="flex items-center gap-1.5" aria-label="Switch app">
    {APPS.map(app => (
      <a
        key={app.id}
        href={app.url}
        onClick={(e) => handleAppNavigation(app.id, app.url, currentApp, e)}
        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full border-2 transition-all touch-manipulation ${
          currentApp === app.id
            ? 'bg-purple-600 border-purple-500 text-white'
            : 'border-slate-600 text-slate-400 hover:border-slate-500 hover:text-slate-300 active:bg-slate-800'
        }`}
        aria-label={`Switch to ${app.label}`}
      >
        <app.icon size={18} strokeWidth={2} />
      </a>
    ))}
  </div>
);

interface HeaderProps {
  onReset: () => void;
  showNewPhoto?: boolean;
  user?: UserProfile | null;
  onLogin: () => void;
  onSignUp: () => void;
  onOpenDashboard: () => void;
  /** When provided, clicking the credits badge opens the Buy Credits modal instead of the full dashboard */
  onOpenBuyCredits?: () => void;
  onNavigateToSettings?: () => void;
  onNavigateToMembers?: () => void;
  onNavigateToReferrals?: () => void;
  isAdmin?: boolean;
  onOpenAdmin?: () => void;
  onLogout?: () => void;
}

const Header: React.FC<HeaderProps> = ({ onReset, showNewPhoto = false, user, onLogin, onSignUp, onOpenDashboard, onOpenBuyCredits, onNavigateToSettings, onNavigateToMembers, onNavigateToReferrals, isAdmin, onOpenAdmin, onLogout }) => {
  const { t } = useTranslation();
  const handleCreditsClick = () => (onOpenBuyCredits ? onOpenBuyCredits() : onOpenDashboard());
  const [accountOpen, setAccountOpen] = useState(false);
  const [billingLoading, setBillingLoading] = useState(false);
  const [billingNotice, setBillingNotice] = useState<string | null>(null);
  const accountRef = useRef<HTMLDivElement>(null);

  const handleBillingPortal = async () => {
    if (billingLoading) return;
    setBillingNotice(null);
    setBillingLoading(true);
    try {
      const result = await openBillingPortal();
      if ('noCustomer' in result) {
        setBillingNotice("No invoices yet — buy credits or a plan and they'll appear here.");
      } else {
        window.location.href = result.url;
      }
    } catch (err) {
      console.error('[Header] Billing portal failed:', err);
      setBillingNotice("Couldn't open billing right now. Please try again.");
    } finally {
      setBillingLoading(false);
    }
  };

  // Persist the chosen language to the signed-in user's profile so it follows
  // them across apps and sessions. Logged-out visitors rely on the cookie.
  const persistLanguage = async (lang: LanguageCode) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        await supabase.from('profiles').update({ language: lang }).eq('id', session.user.id);
      }
    } catch (err) {
      console.warn('Failed to save language preference', err);
    }
  };

  useEffect(() => {
    const close = (e: MouseEvent) => {
      if (accountRef.current && !accountRef.current.contains(e.target as Node)) setAccountOpen(false);
    };
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, []);

  return (
    <header className="sticky top-0 left-0 right-0 bg-black border-b border-slate-900 shrink-0 z-40 flex flex-col md:flex-row md:items-center md:h-[4.5rem] px-4 sm:px-6">
      {/* Mobile: 2 rows. Row 1 = logo + account, Row 2 = app switcher. Desktop: one row */}
      <div className="flex flex-col md:flex-row md:flex-1 md:items-center md:justify-between">
        {/* Row 1: logo + account (both viewports) */}
        <div className="flex items-center justify-between w-full min-h-14 md:min-h-0 py-3 md:py-0 gap-3">
          <div className="flex items-center gap-2 sm:gap-4 min-w-0">
            <div
              className="flex items-center shrink-0 cursor-pointer group h-12 w-[144px] sm:h-[3.6rem] sm:w-[192px]"
              onClick={onReset}
            >
              <img
                src="/Photoclear Logo HR Trans.png"
                alt="PhotoClear"
                className="h-12 sm:h-[3.6rem] w-auto max-w-full object-contain object-left group-hover:opacity-90 transition-all drop-shadow-[0_0_12px_rgba(255,255,255,0.15)]"
              />
            </div>
            <ProductSwitcher currentApp="photoclear" />
          </div>

          <div className="flex items-center gap-2 sm:gap-3 shrink-0 ml-auto" ref={accountRef}>
        <LanguageSwitcher compact onLanguageChange={persistLanguage} />
        {user ? (
          <>
            {onNavigateToReferrals && (
              <button
                onClick={onNavigateToReferrals}
                className="hidden sm:flex items-center gap-1.5 px-3 py-2 bg-emerald-600/20 border border-emerald-500/30 rounded-full text-emerald-300 hover:bg-emerald-600/30 hover:text-emerald-200 transition-all text-xs font-medium min-h-[36px]"
              >
                <Gift size={14} />
                <span>Give 50, Get 50</span>
              </button>
            )}
            {user.credits === 0 ? (
              <button
                onClick={handleCreditsClick}
                className="flex items-center gap-1.5 px-2.5 py-2 pr-3 shrink-0 bg-gradient-to-r from-red-600 to-orange-500 hover:from-red-500 hover:to-orange-400 rounded-full border-2 border-orange-400 transition-all group shadow-lg shadow-orange-500/40 ring-2 ring-orange-400/30 animate-pulse hover:animate-none touch-manipulation min-h-[40px]"
              >
                <div className="w-6 h-6 rounded-full bg-red-700 text-white flex items-center justify-center border border-red-400 shrink-0">
                  <Coins size={12} />
                </div>
                <div className="flex flex-col items-start leading-none text-left">
                  <span className="font-bold text-white text-xs">{t('credits.topUp')}</span>
                  <span className="text-[10px] text-orange-100 font-medium hidden sm:block">0 left</span>
                </div>
              </button>
            ) : (
              <button
                onClick={handleCreditsClick}
                className="flex items-center gap-1.5 px-2.5 py-2 pr-3 shrink-0 bg-orange-600 hover:bg-orange-500 rounded-full border-2 border-orange-400 transition-all group shadow-lg shadow-orange-500/40 ring-2 ring-orange-400/30 touch-manipulation min-h-[40px]"
              >
                <div className="w-6 h-6 rounded-full bg-orange-700 text-white flex items-center justify-center border border-orange-400 shrink-0">
                  <LayoutDashboard size={12} />
                </div>
                <div className="flex flex-col items-start leading-none">
                  <div className="flex items-center gap-1">
                    <span className="font-bold text-white text-sm sm:text-base">{user.credits}</span>
                    <Coins size={12} className="text-white" />
                  </div>
                  <span className="text-[10px] text-orange-100 font-semibold hidden sm:block">{t('credits.label')}</span>
                </div>
              </button>
            )}
            <div className="relative">
              <button
                onClick={() => setAccountOpen((o) => !o)}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-slate-300 hover:text-white hover:bg-slate-800 transition-colors text-sm font-medium min-h-[44px]"
                aria-expanded={accountOpen}
                aria-haspopup="true"
              >
                <span className="max-w-[120px] truncate hidden sm:inline">{user.email || t('auth.account')}</span>
                <ChevronDown size={16} className={`transition-transform ${accountOpen ? 'rotate-180' : ''}`} />
              </button>
              {accountOpen && (
                <div className="absolute right-0 top-full mt-1 py-1 min-w-[180px] bg-slate-900 border border-slate-700 rounded-xl shadow-xl z-50">
                  {onNavigateToSettings && (
                    <button
                      onClick={() => { onNavigateToSettings(); setAccountOpen(false); }}
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-left text-slate-200 hover:bg-slate-800 rounded-lg text-sm"
                    >
                      <Settings size={16} />
                      {t('auth.settings')}
                    </button>
                  )}
                  {onNavigateToMembers && (
                    <button
                      onClick={() => { onNavigateToMembers(); setAccountOpen(false); }}
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-left text-slate-200 hover:bg-slate-800 rounded-lg text-sm"
                    >
                      <Users size={16} />
                      Members
                    </button>
                  )}
                  {onNavigateToReferrals && (
                    <button
                      onClick={() => { onNavigateToReferrals(); setAccountOpen(false); }}
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-left text-slate-200 hover:bg-slate-800 rounded-lg text-sm"
                    >
                      <Gift size={16} />
                      Give 50, Get 50
                    </button>
                  )}
                  <a
                    href="https://hub.rehub.studio/#/partner"
                    className="w-full flex items-center gap-2 px-4 py-2.5 text-left text-slate-200 hover:bg-slate-800 rounded-lg text-sm"
                    onClick={() => setAccountOpen(false)}
                  >
                    <Handshake size={16} />
                    Partner Programme
                  </a>
                  <button
                    onClick={() => { onOpenDashboard(); setAccountOpen(false); }}
                    className="w-full flex items-center gap-2 px-4 py-2.5 text-left text-slate-200 hover:bg-slate-800 rounded-lg text-sm"
                  >
                    <LayoutDashboard size={16} />
                    Dashboard
                  </button>
                  <button
                    onClick={handleBillingPortal}
                    disabled={billingLoading}
                    className="w-full flex items-center gap-2 px-4 py-2.5 text-left text-slate-200 hover:bg-slate-800 rounded-lg text-sm disabled:opacity-60"
                  >
                    {billingLoading ? <Loader2 size={16} className="animate-spin" /> : <Receipt size={16} />}
                    Billing &amp; invoices
                  </button>
                  {billingNotice && (
                    <div className="px-4 py-2 text-xs text-amber-300/90 border-t border-slate-800">{billingNotice}</div>
                  )}
                  {isAdmin && onOpenAdmin && (
                    <button
                      onClick={() => { onOpenAdmin(); setAccountOpen(false); }}
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-left text-slate-200 hover:bg-slate-800 rounded-lg text-sm"
                    >
                      <Shield size={16} />
                      Admin
                    </button>
                  )}
                  <button
                    onClick={() => { setAccountOpen(false); onLogout ? onLogout() : supabase.auth.signOut(); }}
                    className="w-full flex items-center gap-2 px-4 py-2.5 text-left text-slate-200 hover:bg-slate-800 rounded-lg text-sm"
                  >
                    <LogOut size={16} />
                    {t('auth.logout')}
                  </button>
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="flex items-center gap-1.5 sm:gap-3">
            <button
              onClick={onLogin}
              className="flex items-center justify-center gap-1.5 px-3 sm:px-4 py-2.5 min-h-[44px] min-w-[44px] text-slate-300 hover:text-white hover:bg-slate-800 rounded-xl font-medium transition-all text-sm touch-manipulation"
            >
              <LogIn size={18} />
              <span className="hidden sm:inline">{t('auth.login')}</span>
            </button>
            <button
              onClick={onSignUp}
              className="flex items-center justify-center gap-1.5 px-4 sm:px-5 py-2.5 min-h-[44px] bg-gradient-to-r from-blue-600 to-cyan-500 hover:brightness-110 text-white rounded-xl font-semibold transition-all shadow-lg shadow-blue-500/30 text-sm touch-manipulation"
            >
              <UserPlus size={18} className="sm:w-4 sm:h-4" />
              <span>{t('auth.signup')}</span>
            </button>
          </div>
        )}
        {showNewPhoto && (
          <button
            onClick={onReset}
            className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2.5 min-h-[44px] text-sm font-medium text-slate-300 bg-slate-800 border border-slate-700 rounded-xl hover:bg-slate-700 hover:text-orange-300 transition-all touch-manipulation"
          >
            <PlusCircle size={18} />
            <span className="hidden sm:inline">{t('photoclear.newPhoto')}</span>
          </button>
        )}
          </div>
        </div>
      </div>

      {/* Row 2 mobile only: app switcher */}
      <div className="md:hidden border-t border-slate-800 py-2.5 flex items-center justify-center gap-3 overflow-x-auto shrink-0">
        <MobileAppSwitcher currentApp="photoclear" />
      </div>
    </header>
  );
};

export default Header;